# Route: /admin/reason-vocabularies
# List and create reason vocabularies (amendment reason codes, deviation categories).
# Permission: ReasonVocabulary:read
# Feature: F53a G8-a (vocabulary admin — list)

page-reason-vocabulary-list:
  type: Page
  archetype: list
  meta:
    title: $t.admin.reasons.meta_title
    route: /admin/reason-vocabularies
    layout: shell
    auth: required
  data:
    vocabularies:
      model: ReasonVocabulary[]
      refresh: on_mount
      # F53a UX M-01: XP85-DS-007 — explicit data-state declarations.
      data_states:
        loading:
          skeleton_count: 5
        empty:
          ref: reason-list-empty
        error: default
  spatial:
    display: flex
    direction: column
    gap: $spacing.xl
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
    responsive:
      sm:
        padding: $spacing.sm
        padding_inline: $spacing.sm
        gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - reason-list-header
    - reason-list-table

# ── Header ───────────────────────────────────────────────────────────────────

reason-list-header:
  type: PageHeader
  content: $t.admin.reasons.title
  actions:
    - label: $t.admin.reasons.btn_create
      route: /admin/reason-vocabularies/new
      color: info
      # F79 — Cancel / on_success on the create screen returns to this list.
      return_context: origin_only
  hide_actions_on_sm: true

# ── Table ────────────────────────────────────────────────────────────────────

reason-list-table:
  type: DataTable
  bind:
    rows: vocabularies
  empty: reason-list-empty
  # M1 list-filter adoption (DEC-090) — the list's single filter: an opt-in
  # client-side quick-filter over the loaded rows, case-insensitive substring
  # across the visible columns (incl. lifecycle status) + native clear.
  # (Relies on the F83 DataTable transform preserving Filter — fixed in
  # accurentis/XP85 PR #124.)
  filter:
    fields: [displayName, name, versionStatus, description]
    placeholder: $t.common.quick_filter
  columns:
    - key: displayName
      label: $t.admin.reasons.col_display_name
      tokens:
        font_size: $font.size.sm
    - key: name
      priority: medium
      label: $t.admin.reasons.col_name
      tokens:
        font_family: $font.family.mono
        font_size: $font.size.sm
    - key: versionNumber
      label: $t.admin.reasons.col_version
      tokens:
        font_family: $font.family.mono
        font_size: $font.size.sm
    # F92 M-2 — five-state variant chain (Draft/InReview/Approved/Effective/
    # Obsolete), matching the detail pages' chain exactly. "Deprecated" was
    # never a real VersionStatus value (the prior chain's final branch could
    # never match). Obsolete uses "caution", not "stop": it is a routine
    # terminal state (a superseded-but-valid-at-the-time version), not a
    # destructive/error condition — "stop" diverged from the detail pages and
    # over-signalled severity on every list row for what is normal lifecycle
    # churn (F92 UX review M-2).
    - key: versionStatus
      label: $t.admin.reasons.col_status
      width: 1fr
      truncate: true
      display: badge
      variant:
        when: "$row.versionStatus == 'Effective'"
        value: go
        else:
          when: "$row.versionStatus == 'Approved'"
          value: info
          else:
            when: "$row.versionStatus == 'Obsolete'"
            value: caution
            else: info
    # F92 — count of controlled reason codes in this version. No dedicated
    # "count" format exists in XP85 (FormatUtilsGenerator has no formatCount);
    # `entries.length` is a plain dotted column key (SafeColumnNamePattern
    # explicitly documents dots as supported, e.g. "meta.id") resolved via the
    # emitter's existing optional-chain column access — not a new construct.
    - key: entries.length
      label: $t.admin.reasons.col_entries_count
      tokens:
        font_size: $font.size.sm
    - key: description
      priority: low
      label: $t.admin.reasons.col_description
      tokens:
        font_size: $font.size.sm
  row_action:
    type: navigate
    route: /admin/reason-vocabularies/:id
    params:
      id: $row.id
  a11y:
    label: $t.admin.reasons.table_label
  tokens:
    border: $border.default
    radius: $radius.md
    background: $color_scheme.light.color.surface-elevated

reason-list-empty:
  type: EmptyState
  content: $t.admin.reasons.empty
  body: $t.admin.reasons.empty_body
  tokens:
    color: $color_scheme.light.color.on-surface-muted
    font_size: $font.size.md
  spatial:
    padding: $spacing.lg
